import { useCallback, useEffect, useMemo, useState, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { compareDates, getNights, getNightsInRange, isValidDate } from 'src/utils/date'
import { useEditRRForm } from './edit-rr.hook'
import { EditRRSchema, ResourceOccupancySchema, ResourceSchema } from './edit-rr.schema'
import useEditRRQuery from './editrr-query.hook'
import { DateValidationError, PickerChangeHandlerContext } from '@mui/x-date-pickers-pro'
import { Chi } from './edit-rr.types'
import useEditRRMutation from './editrr-mutation.hook'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { LocalizedString, useConfirm } from 'hic-components'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { ReservedResourcePhase } from 'src/api/api.generated'
import { useHotelDate, useTenant } from 'src/app/hooks/context/context.hook'
import { useFrontendPhase, FrontendPhase } from '../../../../utils/phase'
import { MenuActionType } from '../../../../types/guests.types'
import { displayResourceType } from '../../../../../../../../utils/resource-type'
import { useReservedResourceDailyPricesUtils } from 'src/app/hooks/update-reserved-resource/edit-reserved-resource-dates.hook'
import { ResType, ResourceTypeResource } from 'src/app/types/resources.types'
import { ReservedResourceF } from '../../../../types/reservation-detail.types'
import { ReservationContext } from '../../../../context/reservation-context'

export type EditRRProps = {
  propertyId: string
  reservedResource: ReservedResourceF
  open: boolean
  isOnlyChild: boolean
  onClose: () => void
  isTimeResource?: boolean
}

export const useEditRRDrawer = ({ propertyId, reservedResource, isOnlyChild, onClose, isTimeResource }: EditRRProps) => {
  const { id: tenantId } = useTenant()
  const { t } = useTranslation()
  const showError = useErrorSnackBar()
  const localizeString = useLocalizeString()
  const confirm = useConfirm()
  const hotelDate = useHotelDate()
  const [start, setStart] = useState<Date | null>(reservedResource.start)
  const [end, setEnd] = useState<Date | null>(reservedResource.end)
  const [time, setTime] = useState<{ from: Date; to: Date } | undefined>()
  const [adults, setAdults] = useState<number | null>(reservedResource.adults)
  const [children, setChildren] = useState<Chi[]>((reservedResource.childOccupancies ?? []).map((c) => ({ childId: c.child.id, noOccupancy: c.child.noOccupancy, quantity: c.quantity })))
  const [rateId, setRateId] = useState<string | null>(reservedResource.rate.id)
  const [loading, setLoading] = useState(false)
  const [localPhase, setLocalPhase] = useState<ReservedResourcePhase>()
  const [delta, setDelta] = useState<number>(1)
  const [dailyPrices, setDailyPrices] = useState<{ day: Date; price: number }[]>()
  const [deltaDailyPrices, setDeltaDailyPrices] = useState<{ day: Date; price: number }[]>()
  const [skipFirstRateQueryExecution, setSkipFirstRateQueryExecution] = useState<boolean>(true)
  const [timeResourceDailyManualPrice, setTimeResourceDailyManualPrice] = useState<number>(0)

  const { getUpdatedDailyPrices, loading: priceUtilsLoading } = useReservedResourceDailyPricesUtils(tenantId)

  const {optioned} = useContext(ReservationContext).reservation.reservationState

  const { currentPhase, nextPhaseTransitionsAllowed } = useFrontendPhase(reservedResource.start, reservedResource.end, localPhase ? localPhase : reservedResource.phase)

  const timeResourceTypeId =
    isTimeResource && reservedResource?.resourceOccupancies && reservedResource.resourceOccupancies.length > 0 ? reservedResource.resourceOccupancies[0].reservedResourceType.id : undefined

  const {
    rates,
    rate,
    resourceTypes,
    assignableResources,
    resources,
    loading: useEditRRQueryLoading,
  } = useEditRRQuery(
    propertyId, //
    reservedResource,
    start,
    end,
    rateId,
    adults,
    children,
    isTimeResource,
    time,
    timeResourceTypeId,
  )

  const { editRR, deleteRR, performRRCheckinCheckout } = useEditRRMutation(reservedResource)

  const onSubmit = useCallback(
    async (data: EditRRSchema) => {
      if (deltaDailyPrices) {
        setLoading(true)
        const result = await editRR(data, deltaDailyPrices, isTimeResource, timeResourceDailyManualPrice)
        setLoading(false)
        if (result.result === 'SUCCESS') {
          onClose()
        } else if (result.result === 'ERROR') {
          showError(result.error)
        }
      }
    },
    [onClose, deltaDailyPrices, isTimeResource, timeResourceDailyManualPrice],
  )

  const onDelete = useCallback(async () => {
    if (!isOnlyChild) {
      if (reservedResource.phase === 'RESERVATION') {
        setLoading(true)
        const result = await deleteRR()
        setLoading(false)
        if (result.errors) {
          showError(result.errors)
        } else {
          onClose()
        }
      } else {
        showError(t('pms.booking.reservation.reserved_resource_edit.cannot_delete_in_this_phase'))
      }
    } else {
      showError(t('pms.booking.reservation.reserved_resource_edit.cannot_delete_is_only_child'))
    }
  }, [onClose, reservedResource])

  const { control, errors, submitForm, watch, triggerSubmitted, setValue, isSubmitted, trigger } = useEditRRForm(
    reservedResource, //
    resourceTypes,
    assignableResources,
    onSubmit,
    isTimeResource,
  )

  //#region effects on watched form fields

  const formStart = watch('start')
  useEffect(() => {
    if (isValidDate(formStart)) {
      if (!isValidDate(start) || (isValidDate(start) && compareDates(formStart, start) !== 0)) {
        setStart(formStart)
      }
    } else {
      if (isValidDate(start)) {
        setStart(formStart)
      }
    }
  }, [formStart])

  const formEnd = watch('end')
  useEffect(() => {
    if (isValidDate(formEnd)) {
      if (!isValidDate(end) || (isValidDate(end) && compareDates(formEnd, end) !== 0)) {
        setEnd(formEnd)
      }
    } else {
      if (isValidDate(end)) {
        setEnd(formEnd)
      }
    }
  }, [formEnd])

  const formTime = watch('time')
  useEffect(() => {
    if (formTime && isValidDate(formTime.from) && isValidDate(formTime.to)) setTime(formTime)
  }, [formTime])

  const formGuests = watch('guests')
  useEffect(() => {
    if (formGuests) {
      if (formGuests.adults !== adults) {
        setAdults(formGuests.adults)
      }
      if (JSON.stringify(formGuests.children) !== JSON.stringify(children)) {
        setChildren(formGuests.children)
      }
    }
  }, [formGuests])

  const formRateId = watch('rateId')
  useEffect(() => {
    if (formRateId !== rateId) setRateId(formRateId)
  }, [formRateId])

  const formAveragePrice = watch('averagePrice')
  useEffect(() => {
    if (isTimeResource && formAveragePrice) {
      setTimeResourceDailyManualPrice(formAveragePrice)
    }
  }, [formAveragePrice, isTimeResource])

  //#endregion

  const adjustResourceOccupancyStartEndDate = (dateType: 'start' | 'end', date: Date, resourceOccupancies: ResourceOccupancySchema[]): ResourceOccupancySchema[] => {
    const lastROIndex = resourceOccupancies.length - 1
    return [
      ...resourceOccupancies.map((ro, index) => {
        if (dateType === 'start' && index === 0) {
          return { ...ro, start: date }
        }
        if (dateType === 'end' && index === lastROIndex) {
          return { ...ro, end: date }
        }
        return { ...ro }
      }),
    ]
  }

  const onChangeDate = useCallback(
    (onChange: (date: Date | null) => void, dateType: 'start' | 'end', d: Date | null, context?: PickerChangeHandlerContext<DateValidationError>, resourceOccupancies?: ResourceOccupancySchema[]) => {
      if (isValidDate(d)) {
        onChange(d)
        if (resourceOccupancies) {
          trigger()
          const updResourceOccupancies = adjustResourceOccupancyStartEndDate(dateType, d, resourceOccupancies)
          setValue('resourceOccupancies', updResourceOccupancies)
        }
      } else if (context?.validationError?.length) {
        onChange(null)
      }
    },
    [trigger],
  )

  const errorResourceMessage = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const r = errors.resource as any
    return r?.message ?? r?.resourceTypeId?.message ?? r?.resourceId?.message
  }, [errors.resource])

  const formResource = watch('resource')
  const [resourceTypeResource, setResourceTypeResource] = useState<ResourceTypeResource | null>(getResourceTypeResource(localizeString, reservedResource))

  const onResourceTypeResourceChange = useCallback(
    (onChange: (resource: ResourceSchema) => void, v: ResourceTypeResource | null, resourceOccupancies?: ResourceOccupancySchema[]) => {
      if (v === null) {
        if (formResource !== null) {
          onChange(null)
        }
        if (resourceTypeResource !== null) {
          setResourceTypeResource(null)
        }
      } else {
        if (formResource?.resourceTypeId !== v.resourceType.resourceTypeId || formResource?.resourceId !== v.resource?.resourceId) {
          onChange({ resourceTypeId: v.resourceType.resourceTypeId, resourceId: v.resource?.resourceId ?? null })
          if (resourceOccupancies) {
            setValue(
              'resourceOccupancies',
              [...resourceOccupancies]?.map((ro, i) => {
                if (i === 0) {
                  const updRo = { ...ro }
                  updRo.resourceId = v.resource?.resourceId
                  if ((v.resource?.resourceId && v.resource.resourceTypeId !== v.resourceType.resourceTypeId) || ro.reservedResourceTypeId !== v.resourceType.resourceTypeId) {
                    updRo.assignedResourceTypeId = v.resourceType.resourceTypeId
                  }

                  return updRo
                } else {
                  return ro
                }
              }),
            )
          }
        }
        if (JSON.stringify(v) !== JSON.stringify(resourceTypeResource)) {
          setResourceTypeResource(v)
        }
      }
      return
    },
    [formResource, resourceTypeResource],
  )

  const nights: number = useMemo(() => {
    let result = 0
    if (isValidDate(start) && isValidDate(end)) {
      result = getNights(start, end)
    }
    if (isTimeResource) result = result + 1
    return result
  }, [start, end, isTimeResource])

  const initialNights: number = useMemo(() => {
    let response = 0
    if (isValidDate(reservedResource.start) && isValidDate(reservedResource.end)) {
      response = getNights(reservedResource.start, reservedResource.end)
      if (isTimeResource) response = response + 1
    }
    return response
  }, [reservedResource, isTimeResource])

  const [initialPriceValue, setInitialPriceValue] = useState<number | null>(0)
  useEffect(() => {
    let tmpInitial = 0
    if (reservedResource.manualPrice && initialNights) tmpInitial = initialNights * reservedResource.manualPrice
    else if (reservedResource.manualDailyPrices) {
      tmpInitial = reservedResource.manualDailyPrices.reduce((prev, { price }) => prev + price, 0)
    } else if (reservedResource.dailyPricesStored?.dailyPrices) {
      tmpInitial = reservedResource.dailyPricesStored.dailyPrices.reduce((prev, { price }) => prev + price, 0)
    }
    setValue('price', tmpInitial)
    setInitialPriceValue(tmpInitial)
  }, [reservedResource, initialNights])

  const initialAveragePriceValue = useMemo(() => {
    const tmpInitial = initialPriceValue ? initialPriceValue / (initialNights !== nights ? nights : initialNights) : 0
    // console.log('Valore iniziale totale')
    // console.log(initialPriceValue)
    // console.log('Valore start')
    // console.log(start)
    // console.log('Valore end')
    // console.log(end)
    // console.log('Valore nights')
    // console.log(nights)
    // console.log('Valore iniziale medio')
    // console.log(tmpInitial)
    setValue('averagePrice', tmpInitial)
    return tmpInitial
  }, [initialPriceValue, initialNights, nights])

  useEffect(() => {
    setLoading(priceUtilsLoading || useEditRRQueryLoading)
  }, [priceUtilsLoading, useEditRRQueryLoading])

  const recalculatePrices = useCallback(
    async (n: number, adultsParam: number, childrenParam?: Chi[]) => {
      if (!isTimeResource && n > 0 && (formAveragePrice || formAveragePrice === 0) && adultsParam > 0) {
        let response: { day: Date; price: number }[] | undefined = undefined
        if (adultsParam && start && end) {
          // Avoid trigger of rate query on form load
          if (!skipFirstRateQueryExecution) {
            response = await getUpdatedDailyPrices(
              reservedResource.propertyId,
              reservedResource.resourceOccupancies[reservedResource.resourceOccupancies.length - 1].reservedResourceType.id,
              rateId ?? reservedResource.rate.id,
              start ?? reservedResource.start,
              end ?? reservedResource.end,
              reservedResource.start,
              reservedResource.end,
              reservedResource.chargeCategory.id,
              adultsParam,
              childrenParam?.filter((c) => !c.noOccupancy && c.quantity !== null)?.map(({ childId, quantity }) => ({ childId: childId, quantity: quantity as number })), //Typescript limit - cast as number is needed because typescript is not enable to infer narrowing of type applied by filter
              reservedResource.manualPrice,
              reservedResource.manualDailyPrices,
              reservedResource.dailyPricesStored?.dailyPrices,
            )

            if (response) {
              setDailyPrices(response)
            }
          } else {
            setSkipFirstRateQueryExecution(false)
          }
        }

        const updPrice = response ? response.reduce((prev, { price }) => prev + price, 0) : formAveragePrice * n
        setInitialPriceValue(updPrice)
        setValue('price', updPrice)
        setValue('averagePrice', updPrice / n)
      }
    },
    [formAveragePrice, start, end, rateId, skipFirstRateQueryExecution, isTimeResource],
  )

  useEffect(() => {
    if (nights && (isTimeResource || formGuests?.adults)) {
      const tmpAdults = formGuests?.adults ? formGuests.adults : 0
      const tmpchildren = formGuests?.children ? [...formGuests.children] : []
      recalculatePrices(nights, tmpAdults, tmpchildren)
    }
  }, [nights, JSON.stringify(formGuests), isTimeResource])

  const startDisabled = useMemo(() => loading || reservedResource.phase !== ReservedResourcePhase.RESERVATION, [loading, reservedResource.phase])
  const endDisabled = useMemo(() => loading || reservedResource.phase === ReservedResourcePhase.CHECKOUT, [loading, reservedResource.phase])
  const resourceDisabled = useMemo(() => loading || reservedResource.phase !== ReservedResourcePhase.RESERVATION, [loading, reservedResource.phase])

  //#region Phase actions enabled
  const inHouseOption: MenuActionType | undefined = useMemo(() => {
    const allowedHome = nextPhaseTransitionsAllowed.find((t) => t.frontendPhase === FrontendPhase.Home)
    if (allowedHome)
      return {
        action: () => {
          performRRCheckinCheckout(reservedResource.id, allowedHome.action, setLocalPhase)
        },
        disabled: compareDates(reservedResource.start, hotelDate) > 0,
      }
  }, [nextPhaseTransitionsAllowed, reservedResource, hotelDate])

  const notYetArrivedOption: MenuActionType | undefined = useMemo(() => {
    const allowedNotYetArrived = nextPhaseTransitionsAllowed.find((t) => t.frontendPhase === FrontendPhase.NotYetArrived)
    if (allowedNotYetArrived)
      return {
        action: () => {
          performRRCheckinCheckout(reservedResource.id, allowedNotYetArrived.action, setLocalPhase)
        },
      }
  }, [nextPhaseTransitionsAllowed])

  const departedOption: MenuActionType | undefined = useMemo(() => {
    const allowedDeparted = nextPhaseTransitionsAllowed.find((t) => t.frontendPhase === FrontendPhase.Departed)
    if (allowedDeparted)
      return {
        action: () => {
          performRRCheckinCheckout(reservedResource.id, allowedDeparted.action, setLocalPhase)
        },
      }
  }, [nextPhaseTransitionsAllowed])
  //#endregion

  //#region Price changes

  useEffect(() => {
    let rangeOfDailyPrices: { day: Date; price: number }[] = []
    const { manualPrice, manualDailyPrices, dailyPricesStored, start, end } = reservedResource
    if (manualPrice && start && end) {
      // apply same "manualPrice" for every days in reserved resource range.
      const includeEndDate = isTimeResource ? true : false
      rangeOfDailyPrices = getNightsInRange(start, end, includeEndDate).map((d) => ({ day: d, price: manualPrice }))
    } else if (manualDailyPrices && manualDailyPrices.length) {
      rangeOfDailyPrices = manualDailyPrices.map((dp) => dp)
    } else if (dailyPricesStored?.dailyPrices) {
      rangeOfDailyPrices = dailyPricesStored.dailyPrices.map((p) => p)
    }
    setDailyPrices(rangeOfDailyPrices)
  }, [reservedResource.dailyPricesStored?.dailyPrices, reservedResource.manualDailyPrices, reservedResource.manualPrice, reservedResource.start, reservedResource.end, isTimeResource])

  const onDayPriceChangeHandler = useCallback(
    (updDailyPrices: { day: Date; price: number }[]) => {
      setDelta(1)
      setDailyPrices(updDailyPrices)

      const total = updDailyPrices.reduce((previous, { price }) => previous + price, 0)
      setValue('price', total)
      setInitialPriceValue(total)
      setValue('averagePrice', total / (nights ?? 1))
    },
    [nights],
  )

  useEffect(() => {
    if (dailyPrices && (delta || delta === 0)) {
      let residualPrice = dailyPrices.reduce((prev, { price }) => prev + price, 0) * delta
      const result = dailyPrices.map((dp, index) => {
        if (index === dailyPrices.length - 1) {
          return { day: dp.day, price: residualPrice }
        } else {
          const tmpPrice = Math.round(dp.price * delta)
          residualPrice = residualPrice - tmpPrice
          return { day: dp.day, price: tmpPrice }
        }
      })
      setDeltaDailyPrices(result)
    }
  }, [dailyPrices, delta])

  const calculateUniformPrice = useCallback(
    (forcedTotalPrice?: number) => {
      if (deltaDailyPrices) {
        const total = forcedTotalPrice ? forcedTotalPrice : deltaDailyPrices.reduce((prev, { price }) => prev + price, 0)
        const pricePerNight = total / nights
        setDelta(1)
        setDailyPrices(deltaDailyPrices.map((dp) => ({ ...dp, price: pricePerNight })))
        setValue('price', total)
        if (!forcedTotalPrice) setInitialPriceValue(total)
        setValue('averagePrice', pricePerNight)
      }
    },
    [deltaDailyPrices, delta, nights],
  )

  //#endregion

  return {
    loading,
    submitForm,
    confirm,
    onDelete,
    errors,
    control,
    watch,
    onChangeDate,
    triggerSubmitted,
    startDisabled,
    endDisabled,
    formStart,
    resourceDisabled,
    nights,
    resourceTypeResource,
    resourceTypes,
    assignableResources,
    onResourceTypeResourceChange,
    errorResourceMessage,
    resources,
    currentPhase,
    inHouseOption,
    notYetArrivedOption,
    departedOption,
    rate,
    initialPriceValue,
    calculateUniformPrice,
    setValue,
    isSubmitted,
    setDelta,
    initialAveragePriceValue,
    deltaDailyPrices,
    onDayPriceChangeHandler,
    optioned
  }
}

function getResourceTypeResource(localizeString: (localizedString: LocalizedString | null | undefined) => string, reservedResource: ReservedResourceF): ResourceTypeResource {
  const rt = reservedResource.resourceOccupancies[0]
  const resourceType = {
    resourceTypeId: rt.reservedResourceType.id,
    display: displayResourceType(localizeString, rt.reservedResourceType),
    overbooked: true,
    guestsCompatible: false,
    rateCompatible: false,
    maxAdultsCompatible: false,
  }

  const aRt = rt.assignedResourceType
  const assignedResourceType: ResType | undefined = aRt
    ? {
        resourceTypeId: aRt.id,
        display: displayResourceType(localizeString, aRt),
        overbooked: true,
        guestsCompatible: false,
        rateCompatible: false,
        maxAdultsCompatible: false,
        timeConfiguration: aRt.timeConfiguration,
      }
    : undefined

  const r = rt.resource
  const resource = r
    ? {
        resourceId: r.id,
        resourceTypeId: rt.reservedResourceType.id,
        code: r.code,
      }
    : null

  return {
    resourceType,
    resource,
    assignedResourceType,
  }
}
